<template>
  <Modal
    :scrollable-content="false"
    :show="showModal"
    :size="APP_MODAL_SIZES.XL_NEXT"
    data-auto-testid="custom-dashboard-modal"
    data-testid="custom-dashboard-modal"
    manual-close
    style="--modal-body-padding: 0; --modal-header-padding: 40px 32px 12px"
    @close="closeModal"
  >
    <template #loader>
      <SavingIndicator ref="savingIndicatorTop" :type="SAVING_INDICATOR_TYPES.LINE" />
    </template>
    <template #header>
      <div class="em-TitleWrapper">
        <SkeletonItem v-if="loading" height="28px" width="100%" />

        <template v-else>
          <AppInput
            v-model="formModel.name"
            :is-error="!isNameValid"
            :max-length="CUSTOM_DASHBOARD_NAME_MAX_LENGTH"
            :placeholder="$t('dashboard.module_name_placeholder')"
            class="em-Title"
            show-error-for-placeholder
            style-type="title"
            @blur="updateModuleName"
            @focus="isNameValid = true"
          />
          <AppFieldError v-if="!isNameValid" :show="!isNameValid" class="em-TitleError">
            {{ $t('field.required') }}
          </AppFieldError>
        </template>
      </div>
    </template>

    <template #before-close>
      <div v-if="layoutSettingsOptions || columnCountSettings" class="em-BeforeClose">
        <ProgressViewColumnsSelect
          v-if="columnCountSettings"
          :count="columnCountSettings.count"
          :disabled="loading"
          :options="columnCountSettings.options"
          append-label-key="dashboard.default_column_count"
          class="em-BeforeClose_ColumnsSelect"
          @update-count="columnCountSettings.handler"
        />

        <AppSelect
          v-if="layoutSettingsOptions"
          :disabled="loading"
          :model-value="layoutSettingsOptions.selectedValue"
          :offset="[0, 0]"
          :options="layoutSettingsOptions.options"
          :type="SELECT_TYPES.SECONDARY_NEXT"
          append-icon-height="20"
          append-icon-width="20"
          icon="dashboard-view"
          @update:model-value="layoutSettingsOptions.handler"
        >
          <template #button-content="{ option }">
            <AppSelectItem
              v-if="option"
              :show-delete-button="false"
              :show-selected-count-circle="false"
              :type="SELECT_TYPES.SECONDARY_NEXT"
            >
              {{ t('dashboard.default_view') }}: {{ option.label }}
            </AppSelectItem>
          </template>
        </AppSelect>
      </div>
    </template>

    <div>
      <div class="em-Body">
        <SettingsPagesEmptyState
          v-if="isNoElements"
          hero-height="128"
          hero-icon="empty-state-hero"
          hero-width="142"
        >
          <template #title>
            {{ $t('dashboard.no_objectives_yet') }}
          </template>
          <template #subtitle> {{ $t('dashboard.add_first_objective') }}</template>

          <template #action>
            <AppButton
              height="24"
              icon="plus-next"
              type="primary-next"
              width="24"
              @click="openAddObjectives"
            >
              {{ $t('create.objective.btn.add') }}
            </AppButton>
          </template>
        </SettingsPagesEmptyState>
        <TableSortable
          v-if="!isNoElements"
          :columns="TABLE_COLUMNS"
          :data="formModel.elements"
          :loading="loading"
          offset-left="var(--page-left-padding-next)"
          offset-right="var(--page-right-padding-next)"
          order-value-key="orderValueInModule"
          sticky-header
          style="--head-padding-top: 16px; --sticky-z-index: 11"
          @drop="onTableRowDropped"
          @update:data="formModel.elements = $event"
        >
          <template #header-cell="{ column }">
            <template v-if="column.title">
              {{ $t(column.title) }}
            </template>
          </template>
          <template #cell="{ columnKey, item }">
            <template v-if="columnKey === TABLE_COLUMNS_KEYS.NAME">
              <div class="em-OkrName">
                <OkrIcon :objective="item" />
                <TitleItems
                  :id-as-link="false"
                  :objective="item"
                  id-size="small"
                  style="--id-font-weight: 600"
                  with-tooltip
                />
              </div>
            </template>
            <template v-if="columnKey === TABLE_COLUMNS_KEYS.INTERVAL">
              <div
                v-tippy="{
                  content: item.intervalName,
                  placement: 'top-start'
                }"
                class="em-IntervalCell oboard-truncated-text"
              >
                {{ item.intervalName }}
              </div>
            </template>
            <template v-if="columnKey === TABLE_COLUMNS_KEYS.OWNER">
              <div class="em-AvatarWrapper">
                <AppAvatar
                  v-tippy="{
                    placement: 'top',
                    content: item.owner?.displayName
                  }"
                  :avatar-url="item.owner?.avatarUrl"
                  no-margins
                />
              </div>
            </template>
            <template v-if="columnKey === TABLE_COLUMNS_KEYS.GROUPS">
              <div class="em-GroupsList">
                <GroupsCell :item="item" />
              </div>
            </template>
            <template v-if="columnKey === TABLE_COLUMNS_KEYS.ACTION">
              <div class="em-DeleteBtn">
                <AppButton
                  icon="delete-next"
                  size="sm"
                  type="tertiary-next"
                  @click="onRemoveClick(item)"
                />
              </div>
            </template>
          </template>
          <template #loading>
            <LevelsTableLoader />
          </template>
          <template #footer>
            <AppTableCreateButton
              v-tippy="{
                content: addButtonDisabledState.tooltipContent,
                placement: 'top-start',
                offset: [40, 6]
              }"
              :disable="addButtonDisabledState.disabled"
              full-width
              icon-name="plus-next"
              @click="openAddObjectives"
            >
              {{ $t('dashboard.add_elements') }}
            </AppTableCreateButton>
          </template>
        </TableSortable>
      </div>
      <div v-if="!isNoElements" class="em-ModalFooter">
        <AppButton
          v-tippy="{
            content: addButtonDisabledState.tooltipContent
          }"
          :disable="addButtonDisabledState.disabled"
          class="em-ModalFooter_AddObj"
          type="primary-next"
          @click="openAddObjectives"
        >
          {{ $t('dashboard.add_elements') }}
        </AppButton>
      </div>
      <RemoveCustomDashboardElement
        :custom-dashboard-name="modelValue.name"
        :element-for-delete="removeItem"
        :show-confirm-delete-dialog="showRemoveItemModal"
        @close="showRemoveItemModal = false"
        @on-success="onSuccessRemoveElement"
      />
    </div>
  </Modal>
</template>

<script setup>
import { cloneDeep, isEmpty } from 'lodash'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import CustomDashboardApiHandler from '@/api/custom-dashboard'
import { APP_MODAL_SIZES } from '@/utils/components-configurations/app-modal'
import { SELECT_TYPES } from '@/utils/components-configurations/app-select'
import { SAVING_INDICATOR_TYPES } from '@/utils/components-configurations/saving-indicator'
import {
  CUSTOM_DASHBOARD_MODULE_ATTRIBUTES,
  CUSTOM_DASHBOARD_NAME_MAX_LENGTH,
  CUSTOM_DASHBOARD_PROGRESS_VIEW_COLUMN_COUNT_OPTIONS,
  getAddObjectiveButtonDisabledState,
  getLayoutTypeId,
  MODULE_TYPE_IDS
} from '@/utils/custom-dashboard-helper'
import { usePeriodGranulation } from '@/utils/element-progress'
import { handleError } from '@/utils/error-handling'
import { isStringEmpty } from '@/utils/general'
import { showNotify } from '@/utils/notify'
import { CELL_TEXT_ALIGNS, TABLE_COLUMNS_KEYS } from '@/utils/table-columns'

import RemoveCustomDashboardElement from '@/components/dashboard/custom-dashboard/modals/RemoveCustomDashboardElement'
import AppFieldError from '@/components/form/AppFieldError'
import OkrIcon from '@/components/objectives/items/OkrIcon'
import TitleItems from '@/components/objectives/items/TitleItems'
import ProgressViewColumnsSelect from '@/components/objectives/progress-view/ProgressViewColumnsSelect'
import GroupsCell from '@/components/objectives/table/cells/GroupsCell'
import SavingIndicator from '@/components/SavingIndicator'
import AppAvatar from '@/components/ui/AppAvatar/AppAvatar'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppInput from '@/components/ui/AppInput/AppInput'
import AppSelect from '@/components/ui/AppSelect/AppSelect'
import AppSelectItem from '@/components/ui/AppSelect/AppSelectItem'
import AppTableCreateButton from '@/components/ui/AppTableCreateButton'
import Modal from '@/components/ui/Modal/Modal'
import SettingsPagesEmptyState from '@/components/ui/SettingsPagesEmptyState/SettingsPagesEmptyState'
import LevelsTableLoader from '@/components/ui/SkeletonLoaders/LevelsTableLoader'
import SkeletonItem from '@/components/ui/SkeletonLoaders/SkeletonItem'
import TableSortable from '@/components/ui/Table/TableSortable/TableSortable'

defineOptions({
  name: 'EditModule'
})

const emit = defineEmits({
  'update:show-modal': null,
  'update-list': null,
  'open-add-objective-modal': null,
  'module-default-view-updated': null,
  'module-default-column-count-updated': null
})

const props = defineProps({
  showModal: {
    type: Boolean
  },

  modelValue: {
    type: Object,
    default: () => ({})
  },

  dashboardItem: {
    type: Object,
    default: () => ({})
  }
})

const { getPeriodGranulationOptions } = usePeriodGranulation({ pluralizeOptions: true })

const layoutSettingsOptions = computed(() => {
  const createHandler = (attributesOptions = {}) => {
    if (isEmpty(attributesOptions)) return null

    const { attributeKey: key, attributeDefaultValueKey: formModelKey } = attributesOptions

    return {
      selectedValue: formModel.value[formModelKey],
      options: getPeriodGranulationOptions.value,
      handler: value =>
        updateDefaultView({
          formModelKey,
          value,
          key
        })
    }
  }

  if (props.modelValue.typeId === MODULE_TYPE_IDS.KPI) {
    return {
      ...createHandler({ ...CUSTOM_DASHBOARD_MODULE_ATTRIBUTES.GRANULATION_ID }),
      options: getPeriodGranulationOptions.value
    }
  }

  if (props.modelValue.typeId === MODULE_TYPE_IDS.OKR) {
    return {
      ...createHandler({ ...CUSTOM_DASHBOARD_MODULE_ATTRIBUTES.LAYOUT_TYPE_ID }),
      options: getLayoutTypeId.map(item => {
        return {
          ...item,
          label: t(item.tooltip)
        }
      })
    }
  }

  return null
})

const columnCountSettings = computed(() => {
  if (props.modelValue.typeId !== MODULE_TYPE_IDS.KPI) return null

  const { attributeKey: key, defaultValue } = CUSTOM_DASHBOARD_MODULE_ATTRIBUTES.COLUMN_COUNT

  return {
    count: formModel.value[key] ?? defaultValue,
    options: CUSTOM_DASHBOARD_PROGRESS_VIEW_COLUMN_COUNT_OPTIONS,
    handler: value => updateDefaultColumnCount({ value, key })
  }
})

const closeModal = () => {
  emit('update:show-modal', false)
}

const savingIndicatorTop = ref(null)
const formModel = ref({})
const openAddObjectives = () => {
  emit('open-add-objective-modal')
}

watch(
  () => [props.showModal, props.modelValue],
  ([newValueShowModal, newValueModelValue]) => {
    if (newValueShowModal && newValueModelValue) {
      isNameValid.value = true
      formModel.value = cloneDeep(newValueModelValue)
    }
  },
  { deep: true }
)

const showRemoveItemModal = ref(false)
const removeItem = ref({})
const onRemoveClick = item => {
  removeItem.value = {
    ...item,
    dashboardId: props.dashboardItem.id,
    moduleId: props.modelValue.id
  }
  showRemoveItemModal.value = true
}

const TABLE_COLUMNS = [
  {
    title: 'users.table_header_name',
    key: TABLE_COLUMNS_KEYS.NAME,
    width: 'auto'
  },
  {
    title: 'objectives.table_header_interval',
    key: TABLE_COLUMNS_KEYS.INTERVAL,
    width: 120
  },
  {
    title: 'objectives.table_header_assignee',
    key: TABLE_COLUMNS_KEYS.OWNER,
    width: 90,
    textAlign: CELL_TEXT_ALIGNS.CENTER
  },
  {
    title: 'objectives.table_header_groups',
    key: TABLE_COLUMNS_KEYS.GROUPS,
    width: 150
  },
  {
    key: TABLE_COLUMNS_KEYS.ACTION,
    width: 32
  }
]

const isNoElements = computed(() => isEmpty(props.modelValue.elements) && !loading.value)
const loading = ref(false)
const onTableRowDropped = async ({ item, orderValue }) => {
  try {
    const api = new CustomDashboardApiHandler()

    await api.setOrderForElement({
      dashboardId: props.dashboardItem.id,
      moduleId: props.modelValue.id,
      elementId: item.id,
      orderValue
    })

    emit('update-list')
  } catch (error) {
    handleError({ error })
  }
}

const onSuccessRemoveElement = () => {
  showRemoveItemModal.value = false
  emit('update-list')
}

const isNameValid = ref(true)
const updateModuleName = async () => {
  if (isStringEmpty(formModel.value.name)) {
    isNameValid.value = false
    return
  }

  try {
    const api = new CustomDashboardApiHandler()

    await api.updateModule({
      ...props.modelValue,
      name: formModel.value.name,
      moduleId: props.modelValue.id,
      id: props.dashboardItem.id
    })

    emit('update-list')
  } catch (error) {
    handleError({ error })
  }
}

const { t } = useI18n()

const updateDefaultView = async ({ formModelKey, value, key } = {}) => {
  if (!key || !value || !formModelKey) return

  if (formModel.value[formModelKey] === value) return

  loading.value = true
  try {
    formModel.value[formModelKey] = value

    const api = new CustomDashboardApiHandler()

    await api.updateModule({
      [key]: value,
      moduleId: props.modelValue.id,
      id: props.dashboardItem.id
    })

    emit('module-default-view-updated')

    showNotify({
      title: t('workspaces.workspace_field_updated', {
        fieldName: t('dashboard.default_view')
      })
    })
  } catch (error) {
    handleError({ error })
    formModel.value[formModelKey] = props.modelValue[formModelKey]
  } finally {
    loading.value = false
  }
}

const updateDefaultColumnCount = async ({ value, key } = {}) => {
  if (!key || !value) return

  if (formModel.value[key] === value) return

  loading.value = true
  try {
    formModel.value[key] = value

    const api = new CustomDashboardApiHandler()

    await api.updateModule({
      [key]: value,
      moduleId: props.modelValue.id,
      id: props.dashboardItem.id
    })

    emit('module-default-column-count-updated')

    showNotify({
      title: t('workspaces.workspace_field_updated', {
        fieldName: t('dashboard.default_column_count')
      })
    })
  } catch (error) {
    handleError({ error })
    formModel.value[key] = props.modelValue[key]
  } finally {
    loading.value = false
  }
}

const addButtonDisabledState = computed(() => {
  const { elements = [] } = formModel.value

  const { disabled, tooltipContent } = getAddObjectiveButtonDisabledState({
    alreadyAddedObjectivesCount: elements.length
  })

  return {
    disabled,
    tooltipContent
  }
})
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/app-table-helpers';

.em-Body {
  padding: 0 40px;
  min-height: calc(100vh - 156px); // 84px for header, 72px for footer
  z-index: 1;
  position: relative;
}

.em-ModalFooter {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  position: sticky;
  z-index: 10;
  bottom: 0;
  background: $white;
  padding: 20px 0;
  box-shadow: inset 0 1px 0 $grey-2-next;
}

.em-ModalFooter_AddObj {
  margin: 0 40px 0 0;
}

.em-OkrName {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 8px;
}

.em-GroupsList {
  padding-right: 12px;
  display: flex;
}

.em-IntervalCell {
  padding-right: 8px;
}

.em-AvatarWrapper {
  display: flex;
  justify-content: center;
}

.em-DeleteBtn {
  @extend %sortable-table-hidden-items;
}

.em-TitleWrapper {
  width: 100%;
  margin-block: auto;
  z-index: 2;
}

.em-TitleError {
  position: absolute;
  pointer-events: none;
  padding: 5px 10px;
}

.em-Title {
  --input-font-size: #{$fs-24};
  --input-line-height: 28px;
  --input-wrapper-height: 28px;
  --input-wrapper-hover-background-color: transparent;
  --error-border-color: transparent;
}

.em-BeforeClose {
  display: flex;
  align-items: center;
  gap: 8px;
}

.em-BeforeClose_ColumnsSelect {
  --pvc-trigger-background: #{$grey-3-next};
  --pvc-counter-size: 32px;
  --pvc-counter-padding-inline: 8px 5px;
}
</style>
